import React, {
  cloneElement,
  forwardRef,
  Fragment,
  isValidElement,
  ReactNode,
  useLayoutEffect,
  useMemo,
  useRef
} from 'react'
import tw from 'tailwind-styled-components'
import {HTMLUtil, Punycode, punycodeUrlRe, urlRe, Util} from 'yougile-util'
import {IconTabler} from '@components/common/icon-tabler/icon-tabler'
import {noPropagate} from '@containers/hooks'
import {cn} from '@containers/html-utils'
import {EmojiUtils} from '@extensions/emoji-utils'
import {TextWithUrlProps, UpdateData} from './types'

const parseEmoji = (text: string): ReactNode[] => {
  // Escape HTML before parsing emoji because `parse` is unsafe
  text = HTMLUtil.escapeHtml(text)
  text = EmojiUtils.parse(text)

  let i = 0
  const parts: ReactNode[] = []
  for (const match of text.matchAll(EmojiUtils.emojiRe)) {
    const index = match.index ?? 0
    const length = match[0].length
    const before = text.slice(i, index)
    before && parts.push(HTMLUtil.unescapeHtmlUnsafe(before))
    parts.push(
      <img draggable="false" className="emoji" alt={match[1]} src={match[2]} />
    )
    i = index + length
  }

  if (i < text.length) {
    const before = text.slice(i)
    before && parts.push(HTMLUtil.unescapeHtmlUnsafe(before))
  }

  return parts
}

const httpProtoPrefixRe = /^https?:\/\//i

const cleanupUrl = (url: string): string => url.replace(httpProtoPrefixRe, '')

const anyProtoPrefixRe = /^[a-z][a-z0-9]{1,15}:\/\//i

const prefixUrl = (url: string): string =>
  url.match(anyProtoPrefixRe) ? url : 'https://' + url

const commonLinkTitleCs = cn(
  'px-4',
  '-mx-4',
  'break-words',
  'rounded-4',
  'text-secondary',
  'hover:text-secondary',
  'active:text-secondary',
  'visited:text-secondary'
)

const LinkTitle = forwardRef<HTMLElement, {url: string; asLink: boolean}>(
  ({url, asLink}, ref) => {
    const title = Util.limitStr(cleanupUrl(url), 45)

    return asLink ? (
      <a
        ref={ref as React.Ref<HTMLAnchorElement>}
        href={url}
        rel="nofollow"
        target="_blank"
        className={cn(commonLinkTitleCs, 'hover:bg-sticker-blue')}
        onMouseDown={noPropagate}
        onClick={noPropagate}
      >
        {title}
      </a>
    ) : (
      <span ref={ref} className={commonLinkTitleCs}>
        {title}
      </span>
    )
  }
)

const TwLinkIcon = tw(IconTabler)`
  absolute
  block
  w-16
  h-16
  text-action-default
  hover:text-action-hover
  active:text-action-pressed
`

const Link = ({
  url,
  bottom,
  asLink
}: {
  url: string
  bottom: number
  asLink: boolean
}) => {
  // - Firefox doesn't want to work with Punycode-encoded domains, so they must
  //   be decoded first before placing them into `href`-property.
  // - When a URL is not Punycode-encoded it is returned in lowercase thus
  //   breaking YouTube links where both upper&lowercase letters are used.
  const decoded = Punycode.safeDecode(url)
  if (decoded.toLowerCase() !== url.toLowerCase()) {
    url = decoded
  }

  const ref = useRef<HTMLElement>(null)
  const mouseOver = () => ref.current?.classList.add('bg-sticker-blue')
  const mouseOut = () => ref.current?.classList.remove('bg-sticker-blue')

  return (
    <span>
      <LinkTitle ref={ref} url={url} asLink={asLink} />
      &nbsp;
      <a
        href={prefixUrl(url)}
        rel="nofollow"
        target="_blank"
        className="inline-block w-16 relative"
        onMouseDown={noPropagate}
        onClick={noPropagate}
        onMouseOver={mouseOver}
        onMouseOut={mouseOut}
      >
        <TwLinkIcon style={{bottom: `${bottom}px`}} icon="IconCustomLink" />
      </a>
    </span>
  )
}

const parseLinks = (
  text: string,
  re: RegExp,
  options: {limit: number; bottom: number; asLink: boolean}
): {parts: ReactNode[]; data: UpdateData} => {
  const data: UpdateData = {urlAtEnd: true}
  const {limit, bottom, asLink} = options
  let i = 0
  let limited = false
  const parts: ReactNode[] = []

  for (const match of Array.from(text.matchAll(re))) {
    const index = match.index ?? 0
    const length = match[0].length

    let before = text.slice(i, index)
    if (before.length + i > limit) {
      before = Util.limitStr(before, limit - i + 3)
      limited = true
    }
    parts.push(before)

    if (limited) {
      break
    }

    let url = text.slice(index, index + length)
    const spaceBefore = url.match(/^\s+/)
    if (spaceBefore?.length) {
      parts.push(spaceBefore[0])
    }
    url = url.trim()
    parts.push(<Link url={url} bottom={bottom} asLink={asLink} />)
    if (index + length > limit) {
      if (cleanupUrl(url).length < 45 && text.includes(' ', index + length)) {
        parts.push('…')
      }
      limited = true
    } else {
      parts.push(' ')
    }

    i = index + length

    if (limited) {
      break
    }
  }

  if (i < text.length && !limited) {
    let after = text.slice(i)
    if (after.length + i > limit) {
      after = Util.limitStr(after, limit - i + 3)
    }
    parts.push(after)
    data.urlAtEnd = false
  }

  return {parts, data}
}

const mergeUpdateData = (a: UpdateData, b: UpdateData): UpdateData => {
  return {
    urlAtEnd: a.urlAtEnd || b.urlAtEnd
  }
}

export const TextWithUrl = forwardRef<HTMLElement, TextWithUrlProps>(
  (
    {
      text,
      tag,
      className,
      bottom,
      limit = Number.POSITIVE_INFINITY,
      asLink = false,
      onDoubleClick,
      onUpdate,
      onMouseOver,
      onMouseOut
    },
    ref
  ) => {
    const Component: any = tag ?? 'span'

    const {parts, data} = useMemo<{
      parts: ReactNode
      data: UpdateData
    }>(() => {
      text = text.trim()

      const opts = {limit, bottom, asLink}
      let data: UpdateData = {urlAtEnd: false}

      // Punycode links are parsed first because URL regexp may also capture them
      const punyLinkRes = parseLinks(text, punycodeUrlRe, opts)
      data = mergeUpdateData(data, punyLinkRes.data)

      let linkParts: ReactNode[] = []
      punyLinkRes.parts.forEach(part => {
        if (typeof part === 'string') {
          const res = parseLinks(part, urlRe, opts)
          linkParts = linkParts.concat(res.parts)
          data = mergeUpdateData(data, res.data)
        } else {
          linkParts.push(part)
        }
      })

      // Emoji parsing require sanitizing of input, but non-sanitized input is
      // required for link parsing, so we parse links first
      let parts: ReactNode[] = []
      linkParts.forEach(part => {
        if (typeof part === 'string') {
          // Parse only not transformed parts
          const res = parseEmoji(part)
          parts = parts.concat(res)
        } else {
          parts.push(part)
        }
      })

      // Normalize for all items to have a `key` property
      parts = parts.map((x, key) =>
        isValidElement(x) ? (
          cloneElement(x, {key})
        ) : (
          <Fragment key={key}>{x}</Fragment>
        )
      )

      return {parts, data}
    }, [text, limit, bottom, asLink])

    useLayoutEffect(() => {
      onUpdate?.(data)
    }, [data.urlAtEnd])

    return (
      <Component
        ref={ref}
        className={className}
        onDoubleClick={onDoubleClick}
        onMouseOver={onMouseOver}
        onMouseOut={onMouseOut}
      >
        {parts}
      </Component>
    )
  }
)
